import type { HTMLAttributes, ReactNode, Ref } from 'react';
import type { PrimitiveProps } from './Button';
import { RequestId } from './RequestId';
import s from './InlineError.module.css';

export interface InlineErrorProps extends PrimitiveProps<HTMLAttributes<HTMLDivElement>> {
  title?: string;
  children: ReactNode;
  /** The id the operator searches the logs by, so it is set in mono and never translated. */
  requestId?: string;
  action?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * `role="alert"` is written after the spread so no call site can quieten it. This appears
 * in answer to something the reader just did — a create that failed, a rotate the API
 * refused — and it is the only report of it they will get.
 *
 * The request id is the RequestId primitive, as on every other surface that shows one:
 * the same mono id, with one click to copy it. Its label is muted rather than subtle,
 * because this box is transparent and lands on a sunken panel as readily as on the page.
 */
export function InlineError({ title, children, requestId, action, ...rest }: InlineErrorProps) {
  return (
    <div {...rest} role="alert" className={s.error}>
      {title === undefined ? null : <p className={s.title}>{title}</p>}
      <div className={s.body}>{children}</div>
      {requestId === undefined ? null : <RequestId id={requestId} tone="muted" />}
      {action ? <div className={s.action}>{action}</div> : null}
    </div>
  );
}

export default InlineError;
